import {
  EScreenType,
  IActionDefinitionDto,
  IApiManagementDto,
  IDatasourceDto,
  IScreenDesignDto,
  IWorkFlowDto
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { ApiManagementService } from "../../../services/api-management-service";
import { DatasourceService } from "../../../services/datasource-service";
import { ScreenService } from "../../../services/screen-service";
import { WorkFlowService } from "../../../services/workflow-service";
import { ArtifactKind } from "./types";

// A pickable designer artifact: a stable id (stored on the link so it survives renames) + its
// current name (resolved at render time).
export interface ArtifactOption {
  id: string;
  name: string;
}

interface ScreenPublicMeta {
  screenType?: number;
}

// Reports are screens flagged by `screenType` in their publicJson; everything else is a normal
// screen. Defaults to a normal screen when the flag is absent or unparseable.
function screenTypeOf(screen: IScreenDesignDto): number {
  try {
    const meta: ScreenPublicMeta = JSON.parse(screen.publicJson || "{}") as ScreenPublicMeta;
    return meta?.screenType ?? EScreenType.Default;
  } catch {
    return EScreenType.Default;
  }
}

// Concurrent screen loads (the screen + report pickers, or rename resolution for both) share one
// in-flight request, but nothing is cached across loads — so a rename is reflected next time.
const screensInFlight: Map<string, Promise<IScreenDesignDto[]>> = new Map();

function projectScreens(platformProjectId: string): Promise<IScreenDesignDto[]> {
  const existing: Promise<IScreenDesignDto[]> | undefined = screensInFlight.get(platformProjectId);
  if (existing) return existing;
  const pending: Promise<IScreenDesignDto[]> = ScreenService.GetAllByProjectId(platformProjectId)
    .then((response: AxiosResponse<IScreenDesignDto[]>) => response?.data ?? [])
    .finally(() => screensInFlight.delete(platformProjectId));
  screensInFlight.set(platformProjectId, pending);
  return pending;
}

// Per-kind loaders returning {id,name}[] for the artifact picker AND for resolving a linked
// artifact's CURRENT name (so links survive renames). The `component` kind has no project-scoped
// list and falls back to a free-text name.
type ArtifactLoader = (platformProjectId: string) => Promise<ArtifactOption[]>;

const LOADERS: Partial<Record<ArtifactKind, ArtifactLoader>> = {
  // Reports are screens too, so both come off the project's screen list, split by screenType.
  screen: async (platformProjectId: string): Promise<ArtifactOption[]> => {
    const screens: IScreenDesignDto[] = await projectScreens(platformProjectId);
    return screens
      .filter((screen: IScreenDesignDto) => screenTypeOf(screen) !== EScreenType.Report)
      .map((screen: IScreenDesignDto): ArtifactOption => ({ id: screen.id, name: screen.name }));
  },
  report: async (platformProjectId: string): Promise<ArtifactOption[]> => {
    const screens: IScreenDesignDto[] = await projectScreens(platformProjectId);
    return screens
      .filter((screen: IScreenDesignDto) => screenTypeOf(screen) === EScreenType.Report)
      .map((screen: IScreenDesignDto): ArtifactOption => ({ id: screen.id, name: screen.name }));
  },
  // Datasources and their actions are domain-wide (shared across projects); the project id is unused.
  datasource: async (): Promise<ArtifactOption[]> => {
    const response: AxiosResponse<IDatasourceDto[]> = await DatasourceService.GetDatasourcesOfDomain();
    return (response?.data ?? []).map((ds: IDatasourceDto): ArtifactOption => ({ id: ds.id, name: ds.name }));
  },
  // Actions are project-scoped — filtered in the DB query (KAction.KProject_Id), not here.
  action: async (platformProjectId: string): Promise<ArtifactOption[]> => {
    const response: AxiosResponse<IActionDefinitionDto[]> =
      await DatasourceService.GetActionsByProjectId(platformProjectId);
    return (response?.data ?? []).map((action: IActionDefinitionDto): ArtifactOption => ({
      id: action.id,
      name: action.name
    }));
  },
  api: async (platformProjectId: string): Promise<ArtifactOption[]> => {
    const response: AxiosResponse<IApiManagementDto[]> =
      await ApiManagementService.GetAllByProjectId(platformProjectId);
    return (response?.data ?? []).map((api: IApiManagementDto): ArtifactOption => ({ id: api.id, name: api.name }));
  },
  workflow: async (platformProjectId: string): Promise<ArtifactOption[]> => {
    const response: AxiosResponse<IWorkFlowDto[]> = await WorkFlowService.GetAllByProjectId(platformProjectId);
    return (response?.data ?? []).map((wf: IWorkFlowDto): ArtifactOption => ({
      id: wf.id,
      name: wf.displayName || wf.name
    }));
  }
};

// Whether a kind offers a picker (a project/domain-scoped list of existing artifacts) rather than a
// free-text name.
export function artifactKindHasPicker(kind: ArtifactKind): boolean {
  return Boolean(LOADERS[kind]);
}

// Load the existing artifacts of a kind for the picker / name resolution. Returns [] for kinds
// without a loader or on failure (the service already routed the error through the handler).
export async function loadArtifactOptions(kind: ArtifactKind, platformProjectId: string): Promise<ArtifactOption[]> {
  const loader: ArtifactLoader | undefined = LOADERS[kind];
  if (!loader) return [];
  try {
    return await loader(platformProjectId);
  } catch {
    return [];
  }
}
